import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

export type RangeValue = [number | null, number | null]

interface RangeInputProps {
  label: string
  labelMin: string
  labelMax: string
  min: number
  max: number
  step: number
  unit?: string
  value: RangeValue
  onChange: (value: RangeValue) => void
  disabled?: boolean
}

export function RangeInput({
  label,
  labelMin,
  labelMax,
  min,
  max,
  step,
  unit,
  value,
  onChange,
  disabled
}: RangeInputProps) {
  const { t } = useTranslation('application')
  const [lo, hi] = value
  const hasValue = lo != null || hi != null
  const fmt = (n: number | null) =>
    n == null ? '—' : `${n}${unit ? ` ${unit}` : ''}`
  const parse = (raw: string): number | null =>
    raw === '' ? null : Number(raw)

  return (
    <div className="rounded-2xl border border-ink/[0.06] bg-white p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <span className="text-sm font-semibold text-ink">{t(label)}</span>
        {hasValue && (
          <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-primary/10 py-1 pl-3 pr-1.5 text-sm font-bold text-primary">
            {fmt(lo)} – {fmt(hi)}
            {!disabled && (
              <button
                type="button"
                onClick={() => onChange([null, null])}
                aria-label={t('form.common.clear')}
                title={t('form.common.clear')}
                className="flex h-5 w-5 items-center justify-center rounded-full border-0 bg-transparent p-0 text-primary/60 transition-colors hover:bg-primary/15 hover:text-primary"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </span>
        )}
      </div>

      <div className="flex items-end gap-3">
        <div className="flex-1">
          <Label className="mb-1.5 block">{t(labelMin)}</Label>
          <Input
            type="number"
            min={min}
            max={hi ?? max}
            step={step}
            disabled={disabled}
            placeholder={t('form.common.min')}
            value={lo ?? ''}
            onChange={(e) => onChange([parse(e.target.value), hi])}
          />
        </div>
        <span className="pb-2.5 text-ink/30">–</span>
        <div className="flex-1">
          <Label className="mb-1.5 block">{t(labelMax)}</Label>
          <Input
            type="number"
            min={lo ?? min}
            max={max}
            step={step}
            disabled={disabled}
            placeholder={t('form.common.max')}
            value={hi ?? ''}
            onChange={(e) => onChange([lo, parse(e.target.value)])}
          />
        </div>
      </div>
    </div>
  )
}
